import {
	Box,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Select,
	Stack,
	Switch,
	Typography,
	styled,
} from "@mui/material";

export const StyledDialog = styled(Dialog)(({ theme }) =>
	theme.unstable_sx({
		"& .MuiDialog-paper": {
			borderRadius: 3,
			maxHeight: "90vh",
			fontSize: "16px",
			width: "680px",
			maxWidth: "95vw",
			p: "10px",
		},
	}),
);

export const StyledDialogTitle = styled(DialogTitle)(({ theme }) =>
	theme.unstable_sx({
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		py: 2,
		px: 3,
		pb: 2,
	}),
);

export const DialogTitleRow = styled(Stack)(({ theme }) =>
	theme.unstable_sx({
		flexDirection: "row",
		alignItems: "center",
		gap: 2,
	}),
);

export const DialogTitleText = styled(Typography)(({ theme }) =>
	theme.unstable_sx({
		fontWeight: 700,
		fontSize: "22px",
	}),
);

export const OverrideCountChip = styled(Chip)(({ theme }) =>
	theme.unstable_sx({
		fontSize: "13px",
		height: 28,
		verticalAlign: "middle",
		padding: 3,
	}),
);

export const CloseButton = styled(IconButton)(({ theme }) =>
	theme.unstable_sx({
		ml: 2,
	}),
);

export const StyledDialogContent = styled(DialogContent)(({ theme }) =>
	theme.unstable_sx({
		px: 3,
		py: 3,
		pt: 3,
	}),
);

export const LoadingState = styled(Stack)(({ theme }) =>
	theme.unstable_sx({
		alignItems: "center",
		py: 6,
	}),
);

export const LoadingText = styled(Typography)(({ theme }) =>
	theme.unstable_sx({
		fontSize: "15px",
		mt: 2,
	}),
);

export const ErrorState = styled(Stack)(({ theme }) =>
	theme.unstable_sx({
		alignItems: "center",
		py: 4,
		gap: 2,
	}),
);

export const ErrorText = styled(Typography)(({ theme }) =>
	theme.unstable_sx({
		fontSize: "15px",
	}),
);

export const SectionLabel = styled(Typography)(({ theme }) =>
	theme.unstable_sx({
		fontSize: "13px",
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.5px",
		mb: 1,
	}),
);

export const SelectedContractText = styled(Typography)(({ theme }) =>
	theme.unstable_sx({
		fontWeight: 600,
		fontSize: "16px",
	}),
);

export const SelectedContractLabel = styled("span")(({ theme }) =>
	theme.unstable_sx({
		fontSize: "15px",
		fontWeight: 400,
		ml: 2,
		color: "text.secondary",
	}),
);

export const ContractSelectText = styled(Typography)(({ theme }) =>
	theme.unstable_sx({
		fontSize: "15px",
	}),
);

export const ContractSelectLabel = styled("span")(({ theme }) =>
	theme.unstable_sx({
		fontSize: "14px",
		ml: 2,
		color: "text.secondary",
	}),
);

export const ContractSelect = styled(Select)(({ theme }) =>
	theme.unstable_sx({
		mb: 1,
		fontSize: "16px",
	}),
);

export const ContractSelectMenuProps = {
	PaperProps: {
		sx: {
			maxHeight: 350,
		},
	},
} as const;

export const SelectedContractSection = styled(Stack)(({ theme }) =>
	theme.unstable_sx({
		mt: 2,
	}),
);

export const ContractListControls = styled(Stack)(({ theme }) =>
	theme.unstable_sx({
		flexDirection: "row",
		justifyContent: "flex-end",
		mt: 1,
		// refactor with spacing, because . not work and throws errors
		mb: theme.spacing(2),
	}),
);

export const ToggleAllSwitch = styled(Switch)(({ theme }) =>
	theme.unstable_sx({
		mr: "14px",
	}),
);

export const FlagsList = styled(Stack)(({ theme }) =>
	theme.unstable_sx({
		gap: "4px",
	}),
);

type FlagRowProps = {
	modified?: boolean;
};

export const FlagRow = styled(Stack, {
	shouldForwardProp: (prop) => prop !== "modified",
})<FlagRowProps>(({ theme, modified = false }) =>
	theme.unstable_sx({
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		py: 2,
		px: 2,
		borderRadius: "12px",
		bgcolor: modified ? "rgba(237, 108, 2, 0.06)" : "transparent",
		borderLeft: modified ? "3px solid" : "3px solid transparent",
		borderLeftColor: modified ? "warning.main" : "transparent",
		"&:hover": { bgcolor: "action.hover" },
		transition: "background-color 0.15s",
	}),
);

export const FlagDescription = styled(Box)(({ theme }) =>
	theme.unstable_sx({
		minWidth: 0,
		flex: 1,
		mr: 2,
	}),
);

export const FlagLabel = styled(Typography, {
	shouldForwardProp: (prop) => prop !== "modified",
})<FlagRowProps>(({ theme, modified = false }) =>
	theme.unstable_sx({
		fontWeight: modified ? 600 : 400,
		fontSize: "15px",
		lineHeight: 1.4,
	}),
);

export const FlagKey = styled(Typography)(({ theme }) =>
	theme.unstable_sx({
		color: "text.disabled",
		fontFamily: "monospace",
		fontSize: "12px",
		display: "block",
		lineHeight: 1.4,
	}),
);

export const FlagSwitch = styled(Switch)(({ theme }) =>
	theme.unstable_sx({
		flexShrink: 0,
	}),
);

export const EmptyStateText = styled(Typography)(({ theme }) =>
	theme.unstable_sx({
		textAlign: "center",
		py: 4,
	}),
);

export const StyledDialogActions = styled(DialogActions)(({ theme }) =>
	theme.unstable_sx({
		px: 3,
		py: 2,
		justifyContent: "space-between",
	}),
);